import { useMemo } from 'react';
import get from 'lodash/get';

import useViewport from 'Hooks/useViewport';

// These basic settings will be used if none are provided.

// This is what each setting does:
// slidesToShow: Number of slides to show at once
// slidesToScroll: Number of slides to scroll at once
// sliderWidth: Total width of the slider (px)
// dots: Whether to show navigation dots
// arrows: Whether to show navigation arrows
// sliderGap: Gap between slides (px)
// slideVisiblePercent: Percentage of width for slides on mobile devices. (This allows the next slide to partially show).

const DEFAULT_SETTINGS = {
  xs: {
    slidesToShow: 1,
    slidesToScroll: 1,
    sliderWidth: 375,
    dots: false,
    arrows: false,
    sliderGap: 16,
    slideVisiblePercent: 0.85,
  },
  sm: {
    slidesToShow: 2,
    slidesToScroll: 2,
    sliderWidth: 524,
    dots: false,
    arrows: false,
    sliderGap: 16,
    slideVisiblePercent: 0.9,
  },
  md: {
    slidesToShow: 2,
    slidesToScroll: 2,
    sliderWidth: 704,
    dots: false,
    arrows: false,
    sliderGap: 16,
  },
  lg: {
    slidesToShow: 3,
    slidesToScroll: 3,
    sliderWidth: 944,
    dots: true,
    arrows: true,
    sliderGap: 24,
  },
  xl: {
    slidesToShow: 4,
    slidesToScroll: 4,
    sliderWidth: 1124,
    dots: true,
    arrows: true,
    sliderGap: 24,
  },
  xxl: {
    slidesToShow: 4,
    slidesToScroll: 4,
    sliderWidth: 1324,
    dots: true,
    arrows: true,
    sliderGap: 32,
  },
};

/**
 * Custom hook to calculate carousel settings based on viewport
 * @param {Object} sliderSettings - Custom slider settings
 * @param {number} totalSlides - Total number of slides in the carousel
 * @returns {Object} Calculated carousel settings
 */

export default function useSliderSettings(sliderSettings = {}, totalSlides) {
  const { currentBreakpoint } = useViewport();

  // To improve performance and prevent unnecessary re-renders, memoize the settings
  // This will only recalculate when sliderSettings, currentBreakpoint, or totalSlides change
  return useMemo(() => {
    const breakpointDefault = DEFAULT_SETTINGS[currentBreakpoint];
    const breakpointCustom = sliderSettings[currentBreakpoint] || {};

    const settings = {
      slidesToShow: get(breakpointCustom, 'slidesToShow', breakpointDefault.slidesToShow),
      slidesToScroll: get(breakpointCustom, 'slidesToScroll', breakpointDefault.slidesToScroll),
      sliderWidth: get(breakpointCustom, 'sliderWidth', breakpointDefault.sliderWidth),
      sliderGap: get(breakpointCustom, 'sliderGap', breakpointDefault.sliderGap),
      showDots: get(breakpointCustom, 'dots', breakpointDefault.dots),
      showArrows: get(breakpointCustom, 'arrows', breakpointDefault.arrows),
    };

    if (currentBreakpoint === 'xs') {
      settings.slideVisiblePercent = get(sliderSettings.xs, 'slideVisiblePercent', DEFAULT_SETTINGS.xs.slideVisiblePercent);
    }
    if (currentBreakpoint === 'sm') {
      settings.slideVisiblePercent = get(sliderSettings.sm, 'slideVisiblePercent', DEFAULT_SETTINGS.sm.slideVisiblePercent);
    }

    // Hide controls if not needed
    if (totalSlides <= settings.slidesToShow) {
      settings.showDots = false;
      settings.showArrows = false;
    }

    return { settings, defaultSettings: DEFAULT_SETTINGS };
  }, [sliderSettings, currentBreakpoint, totalSlides]);
}
